import React, { lazy, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { popModal } from './globalModalsSlice';
import InviteLinkDialog from '../team/invite/inviteByLinkModal';
import CreateOrUpdateFolder from '../library/folders/CreateOrUpdateFolderDialog';
import DeleteFilesModal from '../selectedVideo/actions/modals/DeleteFileModal';
import DeleteFolderModal from '../library/folders/actions/DeleteFolderModal';
import MoveFileModal from '../selectedVideo/actions/modals/MoveFileModal';
import UploadModal from '../upload/UploadModal';
import ImportModal from '../import/ImportModal';
import EmbedModal from '../selectedVideo/actions/modals/Embed';
import { RootState } from '../../reducers';
import UpgradeSuccessDialog from '../plan/upgrade/UpgradeSuccessDialog';
import UpSellModal from '../dashboard/toolbar/UpSellModal';
import ConnectGoogleCalendarModal from '../settings/meetbot/google/ConnectGoogleCalendarModal';
import FreeTrial from '../plan/upgrade/FreeTrial';
import LoaderComponent from '../app/loader/LoaderReact';
import ConnectMicrosoftTeamsCalendarModal from '../settings/meetbot/microsoft/ConnectMicrosoftTeamsCalendarModal';
import ConnectZoomMeetingsModal from '../settings/meetbot/zoom/ConnectZoomMeetingsModal';
import RecoveryFailureDialog from '../settings/RecoveryFailureDialog';
import RenameFileModal from '../selectedVideo/actions/modals/RenameFileModal';
import TextToSpeechModal from '../textToSpeech/TextToSpeechModal';

// Lazy load upgrade modal with retry logic for module loading failures
const LazyUpgradeModal = lazy(() =>
  import('../plan/upgrade/UpgradeModal').catch((error) => {
    console.error('Failed to load UpgradeModal, reloading page:', error);
    // If import fails, reload the page to get fresh modules
    window.location.reload();
    // Throw to prevent rendering with invalid module
    throw error;
  }),
);

const GlobalModalManager: React.FC = () => {
  const dispatch = useDispatch();
  const modalStack = useSelector(
    (state: RootState) => state.globalModals.modalStack,
  );
  const currentModal = modalStack[modalStack.length - 1];

  const onClose = () => {
    dispatch(popModal());
  };

  if (!currentModal) return null;

  const renderModal = () => {
    switch (currentModal) {
      case 'stripe-pricing-table-upgrade-modal':
        return (
          <Suspense fallback={<LoaderComponent />}>
            <LazyUpgradeModal onClose={onClose} />
          </Suspense>
        );
      case 'import-videos-modal':
        return <UploadModal onClose={onClose} />;
      case 'import-urls-modal':
        return <ImportModal onClose={onClose} />;
      case 'move-file-modal':
        return <MoveFileModal onClose={onClose} />;
      case 'delete-files-modal':
        return <DeleteFilesModal onClose={onClose} />;
      case 'invite-by-link-modal':
        return <InviteLinkDialog onClose={onClose} />;
      case 'create-folder-modal':
        return <CreateOrUpdateFolder onClose={onClose} />;
      case 'delete-folder-modal':
        return <DeleteFolderModal onClose={onClose} />;
      case 'embed-file-modal':
        return <EmbedModal onClose={onClose} />;
      case 'upgradeSuccess':
        return <UpgradeSuccessDialog onClose={onClose} />;
      case 'upsell-modal':
        return <UpSellModal onClose={onClose} />;
      case 'connect-google-calendar-modal':
        return <ConnectGoogleCalendarModal onClose={onClose} />;
      case 'free-trial-modal':
        // Show free trial modal with 7-day trial for all users
        return <FreeTrial onClose={onClose} />;
      case 'connect-microsoft-calendar-modal':
        return <ConnectMicrosoftTeamsCalendarModal onClose={onClose} />;
      case 'connect-zoom-meetings-modal':
        return <ConnectZoomMeetingsModal onClose={onClose} />;
      case 'recovery-failure-modal':
        return <RecoveryFailureDialog onClose={onClose} />;
      case 'rename-file-modal':
        return <RenameFileModal onClose={onClose} />;
      case 'text-to-speech-modal':
        return <TextToSpeechModal onClose={onClose} />;
      default:
        return null;
    }
  };

  return renderModal();
};

export default GlobalModalManager;
